import { useState, useCallback, useMemo } from 'react';
import { textPattern, textPatternMessage, textAreaPattern, textAreaPatternMessage, validateUrlWithTLD, IFieldErrors, ErrorManager } from 'src/types/validation';

type ValidatorFn = (value: string) => string | null;

interface UseBlurValidationReturn {
  blurErrors: Record<string, string | null>;
  handleBlur: (fieldName: string, value: string, validator?: ValidatorFn) => void;
  handleInputBlur: (fieldName: string, value: string, required?: boolean, maxLength?: number) => void;
  handleTextAreaBlur: (fieldName: string, value: string, required?: boolean, maxLength?: number) => void;
  handleUrlBlur: (fieldName: string, value: string, required?: boolean) => void;
  getBlurError: (fieldName: string) => string;
  hasBlurError: (fieldName: string) => boolean;
  clearBlurError: (fieldName: string) => void;
  clearAllBlurErrors: () => void;
  // Server error helpers (from ErrorManager)
  getError: (field: string) => string | undefined;
  hasError: (field: string) => boolean;
  getFieldError: (field: string) => IFieldErrors;
  // Display error helpers - combines server (Zod) and blur errors for UI display only
  getDisplayError: (field: string, serverErrorPath: string) => string | undefined;
  hasDisplayError: (field: string, serverErrorPath: string) => boolean;
}

/**
 * Custom hook for handling onBlur validation across form fields.
 * Provides consistent validation for text inputs and textareas.
 * @param errors - Optional server-side errors from form submission
 */
export const useBlurValidation = (errors?: IFieldErrors): UseBlurValidationReturn => {
  const [blurErrors, setBlurErrors] = useState<Record<string, string | null>>({});
  
  // Memoize ErrorManager result
  const { getError, hasError, getFieldError } = useMemo(() => ErrorManager(errors || {}), [errors]);
  // Generic validator for input fields (default max 255 chars)
  const validateInput = useCallback((value: string, required: boolean = false, maxLength: number = 255): string | null => {
    if (required && (!value || value.trim().length === 0)) {
      return 'This field is required';
    }
    if (value && value.length > maxLength) {
      return `Cannot exceed ${maxLength} characters`;
    }
    if (value && !textPattern.test(value)) {
      return textPatternMessage;
    }
    return null;
  }, []);

  // Generic validator for textarea fields (default max 1000 chars, allows newlines)
  const validateTextArea = useCallback((value: string, required: boolean = false, maxLength: number = 1000): string | null => {
    if (required && (!value || value.trim().length === 0)) {
      return 'This field is required';
    }
    if (value && value.length > maxLength) {
      return `Cannot exceed ${maxLength} characters`;
    }
    if (value && !textAreaPattern.test(value)) {
      return textAreaPatternMessage;
    }
    return null;
  }, []);

  // Handle blur with custom validator
  const handleBlur = useCallback((fieldName: string, value: string, validator?: ValidatorFn) => {
    const error = validator ? validator(value) : validateInput(value);
    setBlurErrors(prev => ({ ...prev, [fieldName]: error }));
  }, [validateInput]);

  // Handle blur for standard input fields
  const handleInputBlur = useCallback((fieldName: string, value: string, required: boolean = false, maxLength: number = 255) => {
    const error = validateInput(value, required, maxLength);
    setBlurErrors(prev => ({ ...prev, [fieldName]: error }));
  }, [validateInput]);

  // Handle blur for textarea fields
  const handleTextAreaBlur = useCallback((fieldName: string, value: string, required: boolean = false, maxLength: number = 1000) => {
    const error = validateTextArea(value, required, maxLength);
    setBlurErrors(prev => ({ ...prev, [fieldName]: error }));
  }, [validateTextArea]);

  // Handle blur for URL fields with TLD validation
  const handleUrlBlur = useCallback((fieldName: string, value: string, required: boolean = true) => {
    const error = validateUrlWithTLD(value, required);
    setBlurErrors(prev => ({ ...prev, [fieldName]: error }));
  }, []);

  // Get error message for a field
  const getBlurError = useCallback((fieldName: string): string => {
    return blurErrors[fieldName] || '';
  }, [blurErrors]);

  // Check if field has an error
  const hasBlurError = useCallback((fieldName: string): boolean => {
    return !!blurErrors[fieldName];
  }, [blurErrors]);

  // Clear error for a specific field
  const clearBlurError = useCallback((fieldName: string) => {
    setBlurErrors(prev => ({ ...prev, [fieldName]: null }));
  }, []);

  // Clear all errors
  const clearAllBlurErrors = useCallback(() => {
    setBlurErrors({});
  }, []);

  // Get error for display - shows server error (from Zod on Next) OR blur error, whichever exists
  const getDisplayError = useCallback((field: string, serverErrorPath: string): string | undefined => {
    const serverError = getError(serverErrorPath);
    if (serverError) return serverError;
    return blurErrors[field] || undefined;
  }, [getError, blurErrors]);

  // Check if field has any error to display (server or blur)
  const hasDisplayError = useCallback((field: string, serverErrorPath: string): boolean => {
    return hasError(serverErrorPath) || !!blurErrors[field];
  }, [hasError, blurErrors]);

  return {
    blurErrors,
    handleBlur,
    handleInputBlur,
    handleTextAreaBlur,
    handleUrlBlur,
    getBlurError,
    hasBlurError,
    clearBlurError,
    clearAllBlurErrors,
    getError,
    hasError,
    getFieldError,
    getDisplayError,
    hasDisplayError
  };
};
